// Edit business text, services and photos here.
export const BUSINESS = {
  name: "NE Tool Repair",
  domain: "https://netoolrepair.co.uk",
  area: "Newcastle upon Tyne & the North East",
  whatsappNumber: "447464554850",
  whatsappDisplay: "07464 554850",
  whatsappMessage: "Hi NE Tool Repair, I'd like to ask about a tool repair.",
};

export const whatsappLink = `https://wa.me/${BUSINESS.whatsappNumber}?text=${encodeURIComponent(BUSINESS.whatsappMessage)}`;

export const NAV = [
  { label: "Home", to: "/" },
  { label: "Repairs & Services", to: "/repairs-services" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

// status: "available" | "development"
export const SERVICES = [
  {
    code: "01",
    title: "Diagnosis",
    status: "available",
    text: "Fault-finding on Milwaukee M12 and M18 tools. Tell us what the tool is doing, send a photo or two, and we'll talk you through what we find.",
    involves: ["Talking through the symptoms with you", "Checking the tool over on the bench", "Explaining what we find and the likely options"],
    signs: ["Tool won't start or cuts out", "Unusual noise, grinding or vibration", "Loss of power or torque", "Overheating or burning smell"],
  },
  {
    code: "02",
    title: "Mechanical repairs",
    status: "available",
    text: "Worn or damaged mechanical parts such as chucks, gearboxes, triggers and housings, assessed case by case.",
    involves: ["Stripping the tool down", "Replacing damaged or worn parts", "Casing and housing replacement", "Rebuilding and checking the tool"],
    signs: ["Cracked or broken casing after a drop or impact", "Chuck or anvil that slips or won't grip", "Sticking or faulty trigger", "Gearbox noise or jamming"],
    example: { label: "Case study 001: casing replacement", to: "/case-studies/milwaukee-m18-impact-wrench-casing-replacement" },
  },
  {
    code: "03",
    title: "Servicing",
    status: "available",
    text: "Routine cleaning, inspection and lubrication for tools that work hard every day.",
    involves: ["Strip down and clean", "Inspection of internal parts", "Cleaning and regreasing", "Reassembly and checks"],
    signs: ["Tool used daily on site", "Dust or debris build-up", "Feels rougher or noisier than it used to"],
  },
  {
    code: "04",
    title: "Refurbishment",
    status: "available",
    text: "Bringing tired, well-used tools back to a clean, tidy working condition.",
    involves: ["Full strip down", "Cleaning of parts and internals", "Replacing tired or damaged outer parts", "Regrease and rebuild"],
    signs: ["Heavily worn, scuffed or battered tools", "Damaged housings or labels", "Tools you want back in good shape"],
  },
];

export const PROCESS = [
  { n: "01", title: "Send your enquiry", text: "Use the repair form or WhatsApp. Include the brand, model and what's going wrong. Photos help a lot." },
  { n: "02", title: "We review it", text: "We look over the details and get back to you using the contact method you prefer." },
  { n: "03", title: "Agree next steps", text: "We talk through the likely options and how to get the tool to us in the North East." },
  { n: "04", title: "Repair & return", text: "The work is carried out and checked, then the tool comes back to you." },
];

// Replace src: null with a path like "/images/m18-before.jpg" once photos are uploaded.
export const BEFORE_AFTER = {
  title: "Milwaukee M18 FUEL 3/4\" impact wrench",
  summary: "Damaged after being run over. The outer casing was replaced with genuine Milwaukee parts, and all the parts and internals were cleaned and greased before the tool was put back together.",
  before: { src: "/images/m18-impact-before.webp", alt: "Worn Milwaukee M18 FUEL impact wrench before refurbishment, with scuffed red housing and tarnished nose" },
  after: { src: "/images/m18-impact-after-v2.webp", alt: "Milwaukee M18 FUEL ONE-KEY impact wrench after refurbishment, with clean red housing and fresh labels" },
};

export const GALLERY = [
  { src: "/images/case001-step1.webp", alt: "Case study 001: Milwaukee M18 3/4 inch impact wrench in for casing replacement after being run over", caption: "Casing replacement" },
  { src: "/images/case001-step2.webp", alt: "Damaged outer casing on a Milwaukee M18 3/4 inch impact wrench that had been run over", caption: "The damage" },
  { src: "/images/case001-step3.webp", alt: "Milwaukee M18 impact wrench fully stripped down on the bench, with internals laid out for cleaning", caption: "Strip down" },
  { src: "/images/case001-step4.webp", alt: "Side-by-side before and after of the Milwaukee M18 impact wrench with its new genuine Milwaukee casing", caption: "Rebuilt" },
];

export const MARQUEE = [
  "Milwaukee M12", "Milwaukee M18", "Diagnosis", "Mechanical repairs", "Servicing", "Refurbishment", "Newcastle upon Tyne", "North East England",
];


// Add new case studies to this list; each gets its own page at /case-studies/<slug>.
export const CASE_STUDIES = [
  {
    slug: "milwaukee-m18-impact-wrench-casing-replacement",
    number: "001",
    tool: "Milwaukee M18 3/4″ Impact Wrench",
    title: "Casing Replacement",
    summary: "This Milwaukee M18 3/4″ impact wrench came in after being run over, leaving the outer casing heavily damaged. The aim was to replace the damaged casing and give the tool a new lease of life rather than write it off and replace the entire unit.",
    process: ["Strip Down", "Clean", "Regrease", "Rebuild"],
    cover: GALLERY[0],
    beforeAfter: BEFORE_AFTER,
    steps: [
      { label: "Casing Replacement", title: "Casing Replacement", image: GALLERY[0], text: "This Milwaukee M18 3/4″ impact wrench came in after being run over, leaving the outer casing heavily damaged. The aim was to replace the damaged casing and give the tool a new lease of life rather than write it off and replace the entire unit." },
      { label: "The Damage", title: "The Damage", image: GALLERY[1], text: "The tool had suffered significant external damage after being run over. The casing was badly scuffed and marked, affecting its overall condition. Before carrying out the repair, the tool was stripped down to access the internal components and prepare for the casing replacement." },
      { label: "Strip Down", title: "Full Disassembly", image: GALLERY[2], text: "The impact wrench was carefully dismantled, separating the casing and exposing the internal components. The parts were laid out for inspection, allowing the tool to be cleaned and prepared for reassembly. This step-by-step approach helps ensure the repair is carried out properly, with care taken over the internal mechanisms and electronics." },
      { label: "Before & After", title: "Restored & Rebuilt", image: GALLERY[3], text: "With the damaged casing replaced, the internal components cleaned, fresh grease applied and the tool rebuilt, the impact wrench was transformed from its heavily damaged external condition. This repair demonstrates how replacing damaged components and carrying out a careful rebuild can be a practical alternative to replacing an entire power tool." },
    ],
  },
];

export const CASE_CLOSING = {
  text: "At NE Tool Repair, we believe in repairing tools wherever practical. Every repair starts with an assessment, followed by careful disassembly, cleaning, component inspection and reassembly. Our goal is to help extend the working life of power tools through practical repairs and careful workmanship.",
  tagline: ["Practical repairs.", "Careful workmanship.", "Less waste."],
};
